import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import CCNavbar from '../../components/CCNavbar';
import CCFooter from '../../components/CCFooter';
import PageWrap from '../../components/PageWrap';
import useDocumentMeta from '../../hooks/useDocumentMeta';
import routeMetaFor from '../../utils/routeMeta';
import { brandPath, parentRouteHref } from '../../utils/brandRouting';
import { SUPPORT_EMAIL } from '../../data/siteContent';
import { APP_LEGAL, EFFECTIVE_DATE, PUBLISHER } from '../../data/legalContent';

function useCCTheme() {
  useEffect(() => {
    document.body.setAttribute('data-brand', 'cardcycle');
    return () => document.body.removeAttribute('data-brand');
  }, []);
}

const TOC = [
  { id: 'noaccount', title: '1. There is no account to delete' },
  { id: 'erase',     title: '2. Erase all CardCycle data' },
  { id: 'uninstall', title: '3. Uninstalling the app' },
  { id: 'limits',    title: '4. What this cannot remove' },
  { id: 'payments',  title: '5. No subscription or purchase' },
  { id: 'contact',   title: '6. Contact' },
];

/**
 * CardCycle data deletion. CardCycle 0.3.0 is local-only and accountless, so
 * deletion happens on the device: there is no server-side account, request
 * workflow, or website form involved.
 */
export default function CCDeletion() {
  useCCTheme();
  const supportHref = parentRouteHref('/contact');
  useDocumentMeta(routeMetaFor('/apps/cardcycle/account-deletion'));

  return (
    <PageWrap testId="cc-deletion-page">
      <CCNavbar />

      <header className="cc-legal-header" style={{ paddingTop: 'calc(var(--nav-h) + 40px)' }}>
        <div className="wrap" style={{ position: 'relative', zIndex: 1 }}>
          <div className="eyebrow" style={{ justifyContent: 'center', color: 'var(--cc-accent)' }}>CardCycle · Legal</div>
          <h1 className="reveal d1">Data Deletion</h1>
          <p className="reveal d2" style={{ marginTop: 8 }} data-testid="cc-deletion-effective">
            Effective date: {EFFECTIVE_DATE} · {APP_LEGAL.cardcycle.version}
          </p>
        </div>
      </header>

      <div className="wrap">
        <div className="cc-summary-box reveal">
          <div className="title">The short version</div>
          <div className="cc-summary-grid">
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>CardCycle {APP_LEGAL.cardcycle.version.replace('CardCycle ', '')} has no account, so there is no server-side account to delete.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>Use <strong>Erase all CardCycle data</strong> in the app to remove its records and scheduled reminders.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>You can also uninstall the app.</span></div>
            <div className="cc-summary-item"><i className="fas fa-check"></i><span>This cannot remove records held outside CardCycle, such as those your card issuer keeps.</span></div>
          </div>
        </div>
      </div>

      <section className="section" style={{ paddingTop: 64, background: 'var(--cc-bg)' }}>
        <div className="wrap">
          <div className="legal-layout">
            <nav className="legal-toc">
              <h6>Contents</h6>
              <ul>{TOC.map(s => <li key={s.id}><a href={`#${s.id}`}>{s.title}</a></li>)}</ul>
            </nav>

            <div className="legal-body" style={{ background: 'var(--cc-surf)', borderColor: 'var(--cc-border)' }}>
              <span className="legal-meta-tag" data-testid="cc-deletion-publisher">Published by {PUBLISHER} · Support: {SUPPORT_EMAIL}</span>

              <section id="noaccount"><h2>1. There is no account to delete</h2>
                <p>{APP_LEGAL.cardcycle.version} works without an account. The records you create stay on your device, so there is no server-side account for us to delete and no deletion request to send us.</p>
                <p>What the app stores on your device is described in the <Link to={brandPath('cardcycle', '/privacy')}>Privacy Notice</Link>. CardCycle is intended for people aged <strong>18 and over</strong>.</p>
              </section>

              <section id="erase"><h2>2. Erase all CardCycle data</h2>
                <p>CardCycle includes an <strong>Erase all CardCycle data</strong> control. Using it removes the records CardCycle holds in the app, together with the reminders it has scheduled.</p>
              </section>

              <section id="uninstall"><h2>3. Uninstalling the app</h2>
                <p>You can also uninstall CardCycle from your device using Android&apos;s normal uninstall process.</p>
              </section>

              <section id="limits"><h2>4. What this cannot remove</h2>
                <div className="callout alert">
                  <i className="fas fa-triangle-exclamation"></i>
                  <span>Erasing data in CardCycle cannot delete information held outside the app&apos;s control.</span>
                </div>
                <p>For example, it does not remove:</p>
                <ul>
                  <li>records your card issuer or bank keeps about your account; or</li>
                  <li>an Android or system-level notification history maintained by your device.</li>
                </ul>
                <p>Those are managed by the organisation or the device feature that holds them.</p>
              </section>

              <section id="payments"><h2>5. No subscription or purchase</h2>
                <p>{APP_LEGAL.cardcycle.version} has no subscription and no in-app purchase, so there is nothing to cancel when you erase your data or uninstall the app.</p>
              </section>

              <section id="contact"><h2>6. Contact</h2>
                <div className="callout info" style={{ background: 'rgba(2,85,230,0.06)', border: '1px solid rgba(2,85,230,0.2)' }}>
                  <i className="fas fa-envelope" style={{ color: 'var(--cc-primary)' }}></i>
                  <span><strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="cc-deletion-email">{SUPPORT_EMAIL}</a><br/>{PUBLISHER}</span>
                </div>
                <p style={{ marginTop: 16, fontSize: '0.85rem' }}>
                  See also the <Link to={brandPath('cardcycle', '/privacy')}>Privacy Notice</Link>, the{' '}
                  <Link to={brandPath('cardcycle', '/terms')}>Terms of Use</Link>, and the{' '}
                  <Link to={brandPath('cardcycle', '/faq')}>CardCycle FAQ</Link>. For anything else, use{' '}
                  {supportHref
                    ? <a href={supportHref}>TickleApps Support</a>
                    : <Link to="/contact">TickleApps Support</Link>}.
                </p>
              </section>
            </div>
          </div>
        </div>
      </section>

      <CCFooter />
    </PageWrap>
  );
}
